/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { forwardRef, useLayoutEffect, useState } from 'react';

import { css, jsx } from '@atlaskit/css';
import Portal from '@atlaskit/portal/portal';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-atlaskit-theme
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { ThemeProvider } from '@atlassian/csm-widget-ui-components/theme-wrapper';
import type { BrandingTheme } from '@atlassian/csm-widget-ui-components/types';
import {
	createNewConversation,
	open,
	show,
} from '@atlassian/customer-service-management-widget-sdk-react/commands';

import {
	AiSearchTriggerContainer,
	useAiSearch,
	useHasIntersectingTrigger,
	useSearchTriggerVisibility,
	useTriggerIntersectionObserver,
} from '../../controllers/ai-search-context';
import { AiSearchInput } from './ai-search-input';

const floatingBoxPositionStyles = css({
	position: 'fixed',
	left: '0',
	right: '0',
	margin: '0 auto',
	bottom: token('space.400'),
	zIndex: layers.modal(),
});

const AiSearchTriggerBase = () => {
	const { ref } = useTriggerIntersectionObserver();

	return <ConnectedAiSearchInput ref={ref} />;
};

type ConnectedAiSearchInputProps = {
	className?: string;
	hidden?: boolean;
};

const hiddenToBottomStyles = css({
	transform: 'translate3d(0, 100%, 0) perspective(1px)',
});

const hiddenToRightStyles = css({
	transform: 'translate3d(50%, 0, 0) perspective(1px)',
});

/**
 * Connected "smart" UI component
 *
 * This component is connected to the AI search context and will update the value and onChange
 * functions when the user types in the input.
 *
 * It also handles the submission of the form and the creation of a new conversation.
 *
 * @private
 */
const ConnectedAiSearchInput = forwardRef<HTMLFormElement, ConnectedAiSearchInputProps>(
	({ className, hidden: hiddenByProp }, ref) => {
		const { isTriggerVisible } = useSearchTriggerVisibility();
		const { value, onChange } = useAiSearch();
		const hideTrigger = hiddenByProp || !isTriggerVisible;

		return (
			<AiSearchInput
				ref={ref}
				hidden={hideTrigger}
				css={[hiddenByProp && hiddenToBottomStyles, !isTriggerVisible && hiddenToRightStyles]}
				// eslint-disable-next-line
				className={className}
				value={value}
				onChange={onChange}
				onSubmit={({ message }) => {
					show();
					open();
					createNewConversation(
						typeof message === 'string' && message.length > 0
							? {
									message,
								}
							: {},
					);
					onChange('');
				}}
			/>
		);
	},
);

const ConditionalThemeProvider = ({
	children,
	themeConfig,
}: {
	children: React.ReactNode;
	themeConfig?: BrandingTheme;
}) => {
	if (themeConfig) {
		return <ThemeProvider themeConfig={themeConfig}>{children}</ThemeProvider>;
	}
	return children;
};

type AiSearchTriggerCoreConfiguration = {
	locale?: string;
	site?: string;
	siteId?: string;
	widgetId?: string;
	widgetTheme?: BrandingTheme;
};

/**
 * The AI search trigger that is connected to trigger the CSM AI Widget when submitted
 * Viewport intersection is tracked to hide any `AiSearchTriggerFloating` that may be simultaneously rendered
 *
 * @public
 */
export const AiSearchTrigger = ({
	locale,
	site,
	siteId,
	widgetId,
	widgetTheme,
}: AiSearchTriggerCoreConfiguration): JSX.Element => (
	<ConditionalThemeProvider themeConfig={widgetTheme}>
		<AiSearchTriggerContainer locale={locale} site={site} siteId={siteId} widgetId={widgetId}>
			<AiSearchTriggerBase />
		</AiSearchTriggerContainer>
	</ConditionalThemeProvider>
);

/**
 * The connected search trigger but connected to the store to hide
 * if there is a visible `AiSearchTrigger` in the viewport
 *
 * @private
 */
const AiSearchTriggerFloatingBase = () => {
	const [mounted, setMounted] = useState(false);
	const hasIntersectingTrigger = useHasIntersectingTrigger();
	const isHidden = !mounted || hasIntersectingTrigger;

	useLayoutEffect(() => {
		setMounted(true);
	}, []);

	return <ConnectedAiSearchInput hidden={isHidden} css={floatingBoxPositionStyles} />;
};

/**
 * A floating (fixed positioned) AI search trigger that is connected to trigger the CSM AI Widget when submitted
 * Visibility is hidden if any currently rendered `AiSearchTrigger` is visible in the viewport (intersecting)
 *
 * @public
 */
export const AiSearchTriggerFloating = ({
	locale,
	site,
	siteId,
	widgetId,
	widgetTheme,
}: AiSearchTriggerCoreConfiguration): JSX.Element => (
	<Portal>
		<ConditionalThemeProvider themeConfig={widgetTheme}>
			<AiSearchTriggerContainer locale={locale} site={site} siteId={siteId} widgetId={widgetId}>
				<AiSearchTriggerFloatingBase />
			</AiSearchTriggerContainer>
		</ConditionalThemeProvider>
	</Portal>
);
